import React, { useMemo } from 'react';
import moment from 'moment';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AlertComponent from '@ui/components/Alert/Alert.component';
import AutocompleteComponent from '@ui/components/Autocomplete/Autocomplete.component';
import InputComponent from '@ui/components/Input/Input.component';
import SelectComponent from '@ui/components/Select/Select.component';
import DateInputComponent from '@ui/components/Date-input/Date-input.component';
import IconSelectComponent from '@ui/features/Icon-select/Icon-select.component';
import UploadDeviceComponent from './Upload-device.component';
import useDictionary from '@ui/hooks/useDictionary';
import { itemTypes } from '@utils/getDeviceObjectKeys';
import { IProps } from './IProps';

const Component: React.FC<IProps> = ({
    lang,
    loading,
    deviceName,
    deviceNameError,
    imei,
    imeiError,
    password,
    passwordError,
    deviceIconNum,
    iconSelectVisible,
    iconSelectEl,
    groupName,
    groups,
    type,
    types,
    additionalInfo,
    keys,
    serverError,
    imeiNotExistError,
    deviceAlreadyExistError,
    deviceAlreadyInUseError,
    noRightsError,
    iconSelectRef,
    uploadError,
    wrongPasswordError,
    onDeviceNameChange,
    onImeiChange,
    onPasswordChange,
    onGroupNameChange,
    onTypeChange,
    onAdditionalInfoChange,
    onAdditionalInfoDateChange,
    onAddClick,
    onInputClick,
    onSelectIconBtnClick,
    onSelectIconWindowClose,
    onIconClick,
    onKeyDown,
    onBtnKeyDown,
    onTemplateDownload,
    onDevicesUpload,
    onSelectKeyDown,
    onClose,
}: IProps) => {
    const getWord = useDictionary();

    const mainAlertMessage = useMemo(() => {
        if (imeiNotExistError) return 'imeiNotExistError';
        else if (deviceAlreadyExistError) return 'deviceAlreadyExistError';
        else if (deviceAlreadyInUseError) return 'deviceAlreadyInUseError';
        else if (noRightsError) return 'noRightsError';
        else if (serverError) return 'serverError';
        else if (uploadError) return 'incorrectFile';
        else if (wrongPasswordError) return 'wrongPasswordError';
        else return '';
    }, [
        imeiNotExistError,
        deviceAlreadyExistError,
        deviceAlreadyInUseError,
        noRightsError,
        serverError,
        uploadError,
        wrongPasswordError,
    ]);

    const additionalInfoElements = useMemo(() => {
        const additionalInfoList = keys.map((item) => {
            switch (item.type) {
                case itemTypes.date:
                    const val =
                        additionalInfo[
                            item.name as keyof typeof additionalInfo
                        ];
                    return (
                        <DateInputComponent
                            lang={lang}
                            label={item.name}
                            id={item.name}
                            key={item.name}
                            timezone={0}
                            value={val ? moment(val, 'DD.MM.YYYY') : null}
                            onChange={onAdditionalInfoDateChange.bind(
                                this,
                                item.name,
                            )}
                            onClick={onInputClick}
                            onFocus={onInputClick}
                            onKeyDown={onKeyDown}
                        />
                    );
                default:
                    return (
                        <InputComponent
                            label={item.name}
                            id={item.name}
                            key={item.name}
                            lang={lang}
                            name={item.name}
                            value={
                                additionalInfo[
                                    item.name as keyof typeof additionalInfo
                                ] ?? ''
                            }
                            onChange={onAdditionalInfoChange}
                            onClick={onInputClick}
                            onFocus={onInputClick}
                            onKeyDown={onKeyDown}
                        />
                    );
            }
        });
        let additionalInfoElements: JSX.Element[] = [];
        for (let i = 0; i < additionalInfoList.length; i++) {
            if ((i + 1) % 2) continue;
            const a = additionalInfoList[i];
            const b = additionalInfoList[i + 1];
            additionalInfoElements.push(
                <Box className="form-row">
                    {a}
                    {b}
                </Box>,
            );
        }
        return additionalInfoElements;
    }, [
        keys,
        lang,
        additionalInfo,
        onAdditionalInfoChange,
        onAdditionalInfoDateChange,
        onInputClick,
        onKeyDown,
    ]);

    return (
        <Box className="add-device__wrap">
            <Box className="add-device">
                <IconButton
                    className="close-btn"
                    onClick={onClose}
                    onKeyDown={onBtnKeyDown}
                >
                    <CloseIcon className="close-btn__ico" />
                </IconButton>
                {!loading && (
                    <>
                        <Box className="add-device__header">
                            <Box className="add-device__title-wrap">
                                <Typography
                                    className="add-device__title"
                                    variant="h6"
                                >
                                    {getWord('addDeviceTitle')}
                                </Typography>
                            </Box>
                            <IconSelectComponent
                                deviceIconNum={deviceIconNum}
                                iconSelectVisible={iconSelectVisible}
                                iconSelectEl={iconSelectEl}
                                iconSelectRef={iconSelectRef}
                                onSelectIconBtnClick={onSelectIconBtnClick}
                                onSelectIconWindowClose={
                                    onSelectIconWindowClose
                                }
                                onIconClick={onIconClick}
                            />
                        </Box>
                        <AlertComponent
                            severity="error"
                            variant="modal"
                            lang={lang}
                            display={!!mainAlertMessage}
                            message={mainAlertMessage}
                        />
                        <FormControl
                            className="add-device__form"
                            component="form"
                        >
                            <Box className="form-row">
                                <InputComponent
                                    label="deviceName"
                                    description="deviceNameTooltip"
                                    name="device-name"
                                    errorMessage="deviceNameError"
                                    lang={lang}
                                    value={deviceName}
                                    error={deviceNameError}
                                    onChange={onDeviceNameChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onKeyDown}
                                />
                                <InputComponent
                                    label="imei"
                                    description="imeiTooltip"
                                    name="imei"
                                    errorMessage="imeiError"
                                    lang={lang}
                                    value={imei}
                                    error={imeiError}
                                    onChange={onImeiChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onKeyDown}
                                />
                            </Box>
                            <Box className="form-row">
                                <InputComponent
                                    label="password"
                                    description="devicePasswordTooltip"
                                    name="device-password"
                                    errorMessage="emptyPasswordError"
                                    type="password"
                                    lang={lang}
                                    value={password}
                                    error={passwordError}
                                    onChange={onPasswordChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onKeyDown}
                                />
                                <AutocompleteComponent
                                    label="group"
                                    name="group"
                                    description="groupTooltip"
                                    lang={lang}
                                    value={groupName}
                                    values={groups.map(({ name }) => name)}
                                    onChange={onGroupNameChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onSelectKeyDown}
                                />
                            </Box>
                            <Box className="form-row">
                                <SelectComponent
                                    label="objectType"
                                    name="object-type"
                                    description="deviceTypeTooltip"
                                    lang={lang}
                                    value={type}
                                    values={types}
                                    onChange={onTypeChange}
                                    onClick={onInputClick}
                                    onFocus={onInputClick}
                                    onKeyDown={onKeyDown}
                                />
                            </Box>
                            {additionalInfoElements}
                        </FormControl>
                        <UploadDeviceComponent
                            onTemplateDownload={onTemplateDownload}
                            onDevicesUpload={onDevicesUpload}
                        />
                        <Box className="add-device__btn-bar">
                            <Button
                                className="add-device__btn"
                                variant="contained"
                                onClick={onAddClick}
                                onKeyDown={onBtnKeyDown}
                            >
                                {getWord('add')}
                            </Button>
                            <Button
                                className="add-device__btn"
                                variant="contained"
                                color="error"
                                onClick={onClose}
                                onKeyDown={onBtnKeyDown}
                            >
                                {getWord('cancel')}
                            </Button>
                        </Box>
                    </>
                )}
                {loading && (
                    <Box className="add-device__loader">
                        <CircularProgress size={50} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const AddDeviceComponent = React.memo(Component);

export default AddDeviceComponent;
